import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown } from 'lucide-react'
import { DetailList } from './detail-list'
import { FlowDone } from './flow-done'
import { FeeReceipt } from './fee-receipt'
import { RouteLine } from './route-line'
import { impactWarns } from '@/lib/price-impact'
import { type Flow, PositionProgress } from './position-progress'
import { useMe } from '@/lib/auth'
import type { PositionView } from '@/lib/api-types'
import { Modal } from './modal'
import { TradeSettings, useSlippageBps } from './trade-settings'
import { Spinner } from './spinner'
import { NumericInput } from './numeric-input'
import { ProgressBar } from './progress-bar'
import { useToast } from './toast'
import { holdingToPicked, SOL_TOKEN, TokenPicker, type PickedToken } from './token-picker'
import { SOL_MINT, USDC_MINT, USDT_MINT } from '@/lib/default-tokens'
import { ChainChip, HeaderRule } from './header-chips'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { Holding, MeWallet } from '@/lib/api-types'
import { formatPct, formatTokenAmount, formatUsd } from '@/lib/format'
import { noteOwnActivity, useInvalidatePortfolio } from '@/lib/portfolio'
import { useRefreshActivity } from '@/lib/activity-feed'
import { amountPart, announceFlow, logoPart, settleAnnounced, withdrawAnnounced, text, type AnnouncedTarget } from '@/lib/announced-flows'
import { isExchangeId, useCustody, usePoolMode, CUSTODY_LABELS, type ExchangeId } from '@/lib/pool'
import { EXCHANGE_COIN_DECIMALS, exchangeCoinOf, isAccountHolding } from '@/lib/networks'
import { PlatformGroup, usePlatformOptions } from './platform-chip'
import { useSetCustody } from '@/lib/custody'
import type { Chain } from '../../../src/constants/chains'

const SOLANA = 'solana' as Chain
const SOL_FEE_RESERVE = 0.005
type Phase =
  | { kind: 'form' }
  | { kind: 'quoting' | 'review' | 'sending' | 'done'; swapId: string }
  | { kind: 'position'; positionId: string; mode: 'open' | 'sell' }
  /** one coin becoming another inside the exchange (Nov 3 2026) */
  | { kind: 'convert'; conversionId: string }
/** SOL, USDC and USDT live in the trading wallet; every other token lives in the vault, bought and sold privately. */
const CORE_MINTS = new Set([SOL_MINT, USDC_MINT, USDT_MINT, SOL_TOKEN.address])

/** the last pair swapped, per browser, so the Swap button opens on it with the amounts empty (John, Nov 1 2026) */
const LAST_SWAP_KEY = 'dispence.swap.last'
type LastSwap = { pay: PickedToken; receive: PickedToken }
function readLastSwap(): LastSwap | null {
  try {
    const raw = localStorage.getItem(LAST_SWAP_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LastSwap>
    return parsed.pay?.address && parsed.receive?.address ? (parsed as LastSwap) : null
  } catch {
    return null
  }
}
function saveLastSwap(pay: PickedToken, receive: PickedToken) {
  try {
    localStorage.setItem(LAST_SWAP_KEY, JSON.stringify({ pay: { ...pay, balance: 0 }, receive: { ...receive, balance: 0 } }))
  } catch {
    // a private window or blocked storage: nothing to remember
  }
}
const PRIVATE_MIN_SOL = 0.02
const CONVERT_TERMINAL = new Set(['completed', 'failed', 'stalled'])
const PRIVATE_EXTRA_SOL = 0.01
/** a flow already running, opened from its chip in the in-progress bar (John, Oct 31 2026) */
export type TrackedSwap = { kind: 'position'; positionId: string; mode: 'open' | 'sell' } | { kind: 'swap'; swapId: string } | { kind: 'conversion'; id: string }

const trim = (v: number, d = 6) => v.toFixed(d).replace(/\.?0+$/, '')
function toRaw(value: string, decimals: number): bigint | null {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(value.trim())
  if (!match || (match[1] === '' && !match[2])) return null
  const raw = BigInt((match[1] || '0') + (match[2] ?? '').slice(0, decimals).padEnd(decimals, '0'))
  return raw > 0n ? raw : null
}

export function SwapModal({
  open,
  onClose,
  wallet,
  holdings,
  initialPay = null,
  track = null,
}: {
  open: boolean
  onClose: () => void
  wallet: MeWallet
  holdings: Holding[]
  /** opened from a holdings row: that token pays and SOL receives; SOL itself leaves the receive side open */
  initialPay?: PickedToken | null
  /** opened from the in-progress bar: straight to that flow's progress, no form */
  track?: TrackedSwap | null
}) {
  const trpc = useTRPC()
  const { user: me } = useMe()
  const slippageBps = useSlippageBps()
  const toast = useToast()
  const invalidatePortfolio = useInvalidatePortfolio()
  const refreshActivity = useRefreshActivity()
  // the bar's chip the moment a button is pressed, before the server answers (John, Nov 5 2026)
  const announcedRef = useRef<number | null>(null)
  const settle = (target: AnnouncedTarget) => {
    if (announcedRef.current !== null) settleAnnounced(announcedRef.current, target)
    announcedRef.current = null
  }
  const unannounce = () => {
    if (announcedRef.current !== null) withdrawAnnounced(announcedRef.current)
    announcedRef.current = null
  }
  const tracking = track !== null
  // pool custody: SOL and the stables sit in the pool too, so every buy is a position and every sale comes out of the pool
  const poolMode = usePoolMode()
  const privateExtraSol = poolMode ? 0.015 : PRIVATE_EXTRA_SOL
  // the account's rows (Nov 3 2026): everything on Solana plus the exchanges' ETH and BNB, which a Convert turns into or out of
  const accountHoldings = useMemo(() => holdings.filter(isAccountHolding), [holdings])
  const solHolding = holdings.find((t) => t.isNative && t.chain === 'solana')
  const sol = useMemo(() => (solHolding ? holdingToPicked(solHolding) : SOL_TOKEN), [solHolding])
  const [pay, setPay] = useState<PickedToken | null>(null)
  const [receive, setReceive] = useState<PickedToken | null>(null)
  const [amount, setAmount] = useState('')
  const [debouncedRaw, setDebouncedRaw] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'form' })
  const [error, setError] = useState<string | null>(null)
  const [positionSettled, setPositionSettled] = useState(false)
  /** the flow's own start as the server stamped it: a view from before it is the wallet's last trade, not this one */
  const [flowStartedAt, setFlowStartedAt] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const [positionStatus, setPositionStatus] = useState<PositionView['status'] | null>(null)
  // a start that was refused shows as the flow's first step failing, not as a form error (John, Nov 1 2026)
  const [startFailed, setStartFailed] = useState(false)
  // the rail a private buy or a vault sale shows from the moment Swap is pressed: the same card then follows the record (John, Nov 1 2026: "one seamless progress UI")
  const [railFlow, setRailFlow] = useState<Flow | null>(null)
  useEffect(() => {
    if (open) {
      const fromToken = !!initialPay && !initialPay.isNative
      // opened from the top button: the last swap's pair comes back, amounts stay empty (John, Nov 1 2026)
      const last = initialPay ? null : readLastSwap()
      const fresh = (t: PickedToken) => {
        if (t.isNative) return sol
        const held = holdings.find((h) => h.chain === 'solana' && !h.isNative && h.address === t.address)
        return held ? holdingToPicked(held) : { ...t, balance: 0 }
      }
      setPay(fromToken ? initialPay : last ? fresh(last.pay) : sol)
      setReceive(fromToken ? sol : last ? fresh(last.receive) : null)
      setPhase(track ? (track.kind === 'position' ? { kind: 'position', positionId: track.positionId, mode: track.mode } : track.kind === 'conversion' ? { kind: 'convert', conversionId: track.id } : { kind: 'sending', swapId: track.swapId }) : { kind: 'form' })
      setAmount(''); setError(null); setPositionSettled(false); setPositionStatus(null); setStartFailed(false); setRailFlow(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, track])
  // keep the pay-side balance fresh after swaps without resetting the form
  useEffect(() => {
    if (pay?.isNative) setPay(sol)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sol.balance])

  const payDecimals = pay?.decimals ?? 9
  // the exchange this swap's SOL, USDC or USDT side uses (John, Oct 31 2026): the account's current one until the chip is clicked
  const { custody, exchange: onExchange } = useCustody()
  const [exchangeId, setExchangeId] = useState<ExchangeId>(isExchangeId(custody) ? custody : 'mexc')
  // the platform this swap's SOL, USDC or USDT side uses (John, Nov 1 2026): any of them, and the pick is remembered
  const platformOptions = usePlatformOptions()
  const { set: setCustody } = useSetCustody()
  const pickPlatform = (id: string) => {
    if (isExchangeId(id)) setExchangeId(id)
    setCustody.mutate({ custody: id as ExchangeId })
  }
  useEffect(() => {
    if (open && isExchangeId(custody)) setExchangeId(custody)
  }, [open, custody])
  const exchangeBalances = useQuery({ ...trpc.vault.exchangeBalances.queryOptions(), enabled: open && onExchange, refetchInterval: 15_000 })
  // the platforms a Convert can run at (MEXC, Coinbase): the others are not offered in that mode (John, Nov 4 2026)
  const convertPlatforms = useQuery({ ...trpc.convert.platforms.queryOptions(), enabled: open && onExchange, staleTime: 5 * 60_000 })
  // balances come from the live holdings, so they move the moment a swap, deposit or withdrawal lands;
  // on the exchange side a core coin's balance is the chosen exchange's
  const liveBalance = (t: PickedToken | null) => {
    if (!t) return 0
    const ledger = onExchange ? exchangeBalances.data?.[exchangeId] : undefined
    const asset = exchangeCoinOf(t)
    if (ledger && asset) return Number(ledger[asset]) / 10 ** EXCHANGE_COIN_DECIMALS[asset]
    const h = holdings.find((x) => isAccountHolding(x) && (t.isNative ? x.isNative : x.address === t.address))
    return h ? Number(h.balance) / 10 ** h.decimals : (t.balance ?? 0)
  }
  const payHeld = liveBalance(pay)
  const receiveHeld = liveBalance(receive)
  const payBalance = pay ? Math.max(0, payHeld - (pay.isNative ? SOL_FEE_RESERVE : 0)) : 0
  const amountNumber = Number(amount) || 0
  // a buy pays SOL for a token; a sale pays a token for SOL, USDC or USDT, the proceeds landing at the exchange in that coin
  // (John, Nov 1 2026: "USDC and USDT, which every CEX supports, should be output options for proceeds")
  const receiveIsCoin = !!receive && (receive.isNative || receive.address === USDC_MINT || receive.address === USDT_MINT)
  // two exchange coins (SOL, USDC, USDT, ETH, BNB): a Convert inside the exchange, no chain touched (John, Nov 3 2026)
  const payCoin = exchangeCoinOf(pay)
  const receiveCoin = exchangeCoinOf(receive)
  const convertMode = onExchange && payCoin !== null && receiveCoin !== null && payCoin !== receiveCoin
  const convertOptions = platformOptions.filter((option) => (convertPlatforms.data ?? []).includes(option.id as ExchangeId))
  useEffect(() => {
    if (!convertMode || convertOptions.length === 0 || convertOptions.some((option) => option.id === exchangeId)) return
    setExchangeId(convertOptions[0].id as ExchangeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertMode, exchangeId, convertOptions.map((option) => option.id).join()])
  const side: 'buy' | 'sell' | null = convertMode ? null : pay && receive ? (pay.isNative && !CORE_MINTS.has(receive.address) ? 'buy' : !CORE_MINTS.has(pay.address) && !pay.isNative && receiveIsCoin ? 'sell' : null) : null
  const tokenAddress = side === 'buy' ? receive?.address : side === 'sell' ? pay?.address : undefined
  const settlement: 'SOL' | 'USDC' | 'USDT' = receive?.address === USDC_MINT ? 'USDC' : receive?.address === USDT_MINT ? 'USDT' : 'SOL'

  useEffect(() => {
    const raw = toRaw(amount, payDecimals)
    const t = setTimeout(() => setDebouncedRaw(raw ? raw.toString() : null), 400)
    return () => clearTimeout(t)
  }, [amount, payDecimals])
  const estimate = useQuery({
    ...trpc.swap.estimate.queryOptions({ inputMint: pay?.address ?? '', outputMint: receive?.address ?? '', amountRaw: debouncedRaw ?? '0', slippageBps }),
    enabled: phase.kind === 'form' && !convertMode && !!pay && !!receive && debouncedRaw !== null && pay.address !== receive.address,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: false,
  })
  // Remove Insider Mark: the bank wallet buys the whole order from the house pool before it sells, with SOL its exchange
  // sends; short of that SOL the sale waits (John, Nov 4 2026: two Kraken sales sat pending "with no warning or something
  // on the swap screen"). Read ahead for the size typed, so the wait is known before Sell.
  const cleanSale = useQuery({
    ...trpc.position.cleanSaleSol.queryOptions({ tokenAddress: pay?.address ?? '', amountRaw: debouncedRaw ?? '0', decimals: pay?.decimals ?? 0, symbol: pay?.symbol?.toUpperCase(), exchange: exchangeId }),
    enabled: phase.kind === 'form' && onExchange && side === 'sell' && !!pay && debouncedRaw !== null && (me?.presets.removeInsiderMark ?? true),
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: false,
  })
  const convertQuote = useQuery({
    ...trpc.convert.quote.queryOptions({ platform: exchangeId, from: payCoin ?? 'SOL', to: receiveCoin ?? 'ETH', amountRaw: debouncedRaw ?? '0' }),
    enabled: phase.kind === 'form' && convertMode && debouncedRaw !== null,
    staleTime: 5_000,
    refetchInterval: 10_000,
    retry: false,
  })
  const receiveDecimals = convertMode ? (receiveCoin ? EXCHANGE_COIN_DECIMALS[receiveCoin] : undefined) : (estimate.data?.outDecimals ?? receive?.decimals ?? undefined)
  const estimatedOut = convertMode
    ? (convertQuote.data?.toAmountUi ?? undefined)
    : estimate.data && receiveDecimals !== undefined && receiveDecimals !== null
      ? Number(estimate.data.outAmountRaw) / 10 ** receiveDecimals
      : undefined
  // Dollar figures: SOL, USDC and USDT have reliable prices; a token is worth
  // what the quote says it is, anchored to the other side less the measured
  // impact, because a token's own price feed can lag its pool by 10% or more.
  const impactPct = estimate.data?.priceImpactPct
  const impactFraction = Math.max(0, Math.min(0.99, (impactPct ?? 0) / 100))
  const payIsCore = !!pay && (pay.isNative || CORE_MINTS.has(pay.address))
  const receiveIsCore = !!receive && (receive.isNative || CORE_MINTS.has(receive.address))
  // Prices follow the live holdings the way balances do: the token picked at open is a snapshot, and SOL picked
  // before the portfolio had loaded carries no price at all (John, Nov 3 2026: "$0 USD output on solana? wtf")
  const livePrice = (t: PickedToken | null) => {
    if (!t) return undefined
    const h = holdings.find((x) => isAccountHolding(x) && (t.isNative ? x.isNative : x.address === t.address))
    return h?.priceUsd || t.priceUsd || undefined
  }
  const payPrice = livePrice(pay)
  const receivePrice = livePrice(receive)
  const payUsdFeed = payPrice ? amountNumber * payPrice : undefined
  const receiveUsdFeed = receivePrice && estimatedOut !== undefined ? estimatedOut * receivePrice : undefined
  // each side as the other side's figure implies through the quote
  const fromReceive = receiveUsdFeed !== undefined ? receiveUsdFeed / (1 - impactFraction) : undefined
  const fromPay = payUsdFeed !== undefined && estimatedOut !== undefined ? payUsdFeed * (1 - impactFraction) : undefined
  // the core side anchors when it has a price; whichever side has no price of its own takes the other side's figure
  const payUsd = convertMode ? (convertQuote.data?.fromUsd ?? payUsdFeed) : payIsCore || !receiveIsCore ? (payUsdFeed ?? fromReceive) : (fromReceive ?? payUsdFeed)
  const receiveUsd = convertMode ? (convertQuote.data?.toUsd ?? undefined) : receiveIsCore || !payIsCore ? (receiveUsdFeed ?? fromPay) : (fromPay ?? receiveUsdFeed)

  // every start and stop reads the lists again at once, so the in-progress bar follows without waiting for a poll (John, Oct 31 2026)
  const create = useMutation(trpc.swap.create.mutationOptions({ onSuccess: (res) => { settle({ kind: 'swap', id: res.swapId }); setPhase({ kind: 'quoting', swapId: res.swapId }); refreshActivity(['swap']) }, onError: (err) => { unannounce(); setError(errorMessage(err)) } }))
  const confirm = useMutation(trpc.swap.confirm.mutationOptions({ onSuccess: () => { setPhase((p) => (p.kind === 'review' ? { kind: 'sending', swapId: p.swapId } : p)); refreshActivity(['swap']) }, onError: (err) => setError(errorMessage(err)) }))
  const cancel = useMutation(trpc.swap.cancel.mutationOptions({ onSuccess: () => refreshActivity(['swap']) }))
  // a bank wallet is reused from one sale to the next: the view cached from its last sale (idle, which reads as done)
  // must never be the first thing the new sale's card sees (John, Nov 4 2026: the bar filled to 100% on step 2)
  const startFlow = (res: { id: string; startedAt: string | null }, mode: 'open' | 'sell') => {
    settle({ kind: 'position', id: res.id, mode })
    queryClient.removeQueries({ queryKey: trpc.position.get.queryKey({ id: res.id }) })
    setFlowStartedAt(res.startedAt)
    setPhase({ kind: 'position', positionId: res.id, mode })
    refreshActivity(['position'])
  }
  const convertCreate = useMutation(
    trpc.convert.create.mutationOptions({
      onSuccess: (res) => {
        settle({ kind: 'conversion', id: res.id })
        setPhase({ kind: 'convert', conversionId: res.id })
        refreshActivity(['swap'])
        void queryClient.invalidateQueries({ queryKey: trpc.convert.list.queryKey() })
      },
      onError: (err) => {
        unannounce()
        setError(errorMessage(err))
      },
    }),
  )
  const conversionId = phase.kind === 'convert' ? phase.conversionId : null
  const conversion = useQuery({
    ...trpc.convert.get.queryOptions({ id: conversionId ?? '' }),
    enabled: conversionId !== null,
    refetchInterval: (q) => (q.state.data && CONVERT_TERMINAL.has(q.state.data.status) ? false : 1_500),
  })
  const cv = conversion.data
  const convertSettled = phase.kind === 'convert' && cv !== undefined && CONVERT_TERMINAL.has(cv.status)
  useEffect(() => {
    if (phase.kind !== 'convert' || !cv) return
    if (cv.status === 'completed') {
      noteOwnActivity([cv.from, cv.to])
      void invalidatePortfolio()
      void queryClient.invalidateQueries({ queryKey: trpc.convert.list.queryKey() })
      void queryClient.invalidateQueries({ queryKey: trpc.vault.exchangeBalances.queryKey() })
    } else if (cv.status === 'failed' || cv.status === 'stalled') {
      toast('error', cv.status === 'failed' ? 'Conversion refused' : 'Conversion needs a look', cv.error ?? undefined)
      void queryClient.invalidateQueries({ queryKey: trpc.convert.list.queryKey() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cv?.status])
  const openPosition = useMutation(trpc.position.open.mutationOptions({ onSuccess: (res) => startFlow(res, 'open'), onError: (err) => { unannounce(); setError(errorMessage(err)); setStartFailed(true) } }))
  const sellFromVault = useMutation(trpc.position.sellFromVault.mutationOptions({ onSuccess: (res) => startFlow(res, 'sell'), onError: (err) => { unannounce(); setError(errorMessage(err)); setStartFailed(true) } }))
  const swapId = phase.kind === 'quoting' || phase.kind === 'review' || phase.kind === 'sending' || phase.kind === 'done' ? phase.swapId : null
  const status = useQuery({
    ...trpc.swap.status.queryOptions({ swapId: swapId ?? '' }),
    enabled: swapId !== null,
    refetchInterval: (q) => {
      const d = q.state.data
      if (d?.status === 'confirmed') return d.checked ? false : 1_500
      return d?.status === 'errored' || d?.status === 'cancelled' ? false : 1_500
    },
  })
  const s = status.data
  useEffect(() => {
    if (!s || phase.kind === 'form' || phase.kind === 'position' || phase.kind === 'convert') return
    if (phase.kind === 'quoting' && s.status === 'pendingUserConfirm' && s.estimatedOutputAmount) {
      if (s.warnings.length === 0) {
        // clean quote: no separate confirm step
        confirm.mutate({ swapId: phase.swapId })
        setPhase({ kind: 'sending', swapId: phase.swapId })
      } else {
        setPhase({ kind: 'review', swapId: phase.swapId })
      }
    } else if (s.status === 'confirmed' && phase.kind !== 'done') {
      setPhase({ kind: 'done', swapId: phase.swapId })
      const inSym = (s.inputTokenSymbol ?? '').toUpperCase()
      const outSym = (s.outputTokenSymbol ?? '').toUpperCase()
      // the bar's finished chip says so (John, Nov 1 2026: no separate snackbars)
      noteOwnActivity([inSym, outSym])
      void invalidatePortfolio()
    } else if (s.status === 'errored' || s.status === 'cancelled') {
      setError(s.error ?? (s.status === 'cancelled' ? 'Swap cancelled.' : 'Swap failed.'))
      setPhase({ kind: 'form' })
      if (s.status === 'errored') toast('error', 'Swap failed', s.error ?? undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.status, s?.estimatedOutputAmount])

  function flip() {
    const nextPay = receive
    const nextReceive = pay
    // the pay side needs a balance; look it up from holdings
    const held = nextPay ? holdings.find((t) => (t.isNative ? SOL_TOKEN.address : t.address) === nextPay.address) : undefined
    setPay(nextPay ? (held ? holdingToPicked(held) : { ...nextPay, balance: 0 }) : null)
    setReceive(nextReceive)
    setAmount('')
  }
  function startSwap() {
    setError(null)
    setStartFailed(false)
    setRailFlow(null)
    if (!pay || !receive) return setError('Pick what to pay and what to receive.')
    if (convertMode && payCoin && receiveCoin) {
      if (!(amountNumber > 0)) return setError('Enter an amount.')
      if (amountNumber > payBalance + 1e-12) return setError('Amount is more than your balance.')
      const raw = toRaw(amount, EXCHANGE_COIN_DECIMALS[payCoin])
      if (!raw) return setError('Enter an amount.')
      if (convertQuote.data && raw < BigInt(convertQuote.data.minFromRaw)) return setError(`${CUSTODY_LABELS[exchangeId]} trades at least ${Number(convertQuote.data.minFromRaw) / 10 ** EXCHANGE_COIN_DECIMALS[payCoin]} ${payCoin} at once.`)
      saveLastSwap(pay, receive)
      announcedRef.current = announceFlow({ kind: 'swap', title: [logoPart({ symbol: payCoin }), text('Convert'), amountPart(payCoin, { amountUi: amountNumber }), text('to'), text(receiveCoin), text('at'), { type: 'place', place: exchangeId }], symbols: [payCoin, receiveCoin] })
      convertCreate.mutate({ walletId: wallet.id, platform: exchangeId, from: payCoin, to: receiveCoin, amountRaw: raw.toString() })
      return
    }
    if (!side || !tokenAddress) return setError('Pay SOL for a token, or a token for SOL, USDC or USDT; token-to-token swaps are coming.')
    if (!(amountNumber > 0)) return setError('Enter an amount.')
    if (amountNumber > payBalance + 1e-12) return setError(pay.isNative ? 'Leave a little SOL for fees.' : 'Amount is more than your balance.')
    saveLastSwap(pay, receive)
    if (vaultSell) {
      const raw = toRaw(amount, payDecimals)
      if (!raw) return setError('Enter an amount.')
      setRailFlow('vaultSell')
      announcedRef.current = announceFlow({ kind: 'sell', title: [logoPart({ symbol: pay.symbol, address: pay.address, imageUrl: pay.imageUrl }), text('Sell'), amountPart(pay.symbol, { amountRaw: raw.toString(), decimals: payDecimals })], symbols: [pay.symbol, 'SOL'] })
      sellFromVault.mutate({ walletId: wallet.id, tokenAddress: pay.address, amountRaw: raw.toString(), slippageBps, exchange: onExchange ? exchangeId : undefined, settlement })
      return
    }
    if (privateBuy) {
      if (amountNumber < PRIVATE_MIN_SOL) return setError(`The smallest private buy is ${PRIVATE_MIN_SOL} SOL.`)
      if (amountNumber > payBalance - privateExtraSol + 1e-12) return setError(poolMode ? 'Leave about 0.02 SOL extra for the network fees.' : 'Leave about 0.01 SOL extra for the private routing and fees.')
      const raw = toRaw(amount, 9)
      if (!raw) return setError('Enter an amount.')
      // on an exchange the bought token stays in its own open wallet (the exchange takes no tokens); on Sonar's pool it is deposited
      setRailFlow(onExchange ? 'buy' : poolMode ? 'vaultBuy' : 'buy')
      announcedRef.current = announceFlow({ kind: 'buy', title: [logoPart({ symbol: receive.symbol, address: tokenAddress, imageUrl: receive.imageUrl }), text('Buy'), amountPart('SOL', { amountRaw: raw.toString(), decimals: 9 })], symbols: [receive.symbol, 'SOL'] })
      openPosition.mutate({ walletId: wallet.id, tokenAddress, tokenSymbol: receive.symbol, amountLamports: raw.toString(), slippageBps, exchange: onExchange ? exchangeId : undefined })
      return
    }
    announcedRef.current = announceFlow({ kind: 'swap', title: [logoPart(side === 'buy' ? { symbol: receive.symbol, address: receive.address, imageUrl: receive.imageUrl } : { symbol: pay.symbol, address: pay.address, imageUrl: pay.imageUrl }), text(side === 'buy' ? 'Buy' : 'Sell'), amountPart(pay.symbol, { amountUi: amountNumber })], symbols: [pay.symbol, receive.symbol] })
    create.mutate({ chain: SOLANA, address: tokenAddress, side, amount: amount.trim(), amountUnit: side === 'buy' ? 'native' : 'token', walletId: wallet.id, clientRequestId: crypto.randomUUID(), quoteFirst: true })
  }
  function close() {
    if (phase.kind === 'review' || phase.kind === 'quoting') cancel.mutate({ swapId: phase.swapId })
    onClose()
  }

  const expiresIn = s?.expiresAt ? Math.max(0, Math.round((s.expiresAt - Date.now()) / 1000)) : null
  const busy = phase.kind === 'quoting' || phase.kind === 'sending' || phase.kind === 'review' || (phase.kind === 'position' && !positionSettled) || (phase.kind === 'convert' && !convertSettled)
  // no toggles: a token buy always runs from a fresh wallet into the vault, a vault token always sells from a fresh wallet
  const privateBuy = side === 'buy' && !!receive && (poolMode ? receive.address !== SOL_MINT : !CORE_MINTS.has(receive.address))
  const vaultSell = side === 'sell' && pay?.custody === 'vault'
  const starting = create.isPending || openPosition.isPending || sellFromVault.isPending || convertCreate.isPending
  function reset() {
    setPhase({ kind: 'form' })
    setAmount('')
    setError(null)
    setStartFailed(false)
    setRailFlow(null)
    setPositionSettled(false)
    setPositionStatus(null)
  }
  // one card from the moment Swap is pressed to the end: it shows the start, then the record, on one rail (John, Nov 1 2026)
  const showRail = phase.kind === 'position' || (railFlow !== null && phase.kind === 'form')
  const finished = phase.kind === 'done' || (phase.kind === 'position' && positionSettled) || convertSettled
  const footer =
    tracking ? (
      // watching a flow that was started earlier: nothing to submit, only the progress to follow
      <button type="button" className={`btn w-full ${finished ? 'btn-primary' : 'btn-secondary'}`} onClick={onClose}>{finished ? 'Done' : 'Close'}</button>
    ) : phase.kind === 'review' ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => { cancel.mutate({ swapId: phase.swapId }); setPhase({ kind: 'form' }) }}>Cancel</button>
        <button type="button" className="btn btn-primary flex-1" disabled={confirm.isPending || expiresIn === 0} onClick={() => { confirm.mutate({ swapId: phase.swapId }); setPhase({ kind: 'sending', swapId: phase.swapId }) }}>
          {confirm.isPending ? <Spinner /> : expiresIn === 0 ? 'Quote expired' : 'Swap anyway'}
        </button>
      </div>
    ) : finished ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={reset}>Swap again</button>
        <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
      </div>
    ) : (
      <button type="button" className="btn btn-primary w-full" disabled={busy || starting || (side === 'sell' && cleanSale.data?.short === true)} onClick={startSwap}>
        {phase.kind === 'quoting'
          ? 'Getting the best route…'
          : phase.kind === 'convert'
            ? 'Converting…'
          : phase.kind === 'sending'
            ? 'Swapping…'
            : phase.kind === 'position'
              ? positionStatus === 'selling'
                ? 'Selling…'
                : positionStatus === 'funded' || positionStatus === 'buying'
                  ? 'Buying…'
                  : positionStatus === 'custodying' || positionStatus === 'returning'
                    ? onExchange ? 'Depositing…' : 'Returning…'
                    : 'Funding…'
              : starting
                ? convertMode ? 'Converting…' : 'Funding…'
                : convertMode ? 'Convert now' : 'Swap now'}
      </button>
    )

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        <span className="flex items-center gap-3">
          {track?.kind === 'position' ? (track.mode === 'sell' ? 'Sell' : 'Buy') : track?.kind === 'conversion' || convertMode ? 'Convert' : 'Swap'}
          {/* the chain, in the header beside the title, then a rule and every exchange this swap's SOL, USDC or USDT side can use, the chosen one lit (John, Nov 1 2026: "move the CEX cycler to the top"; Nov 2 2026: "click to select ... rather than cycle", "a | or separator between those and the Sol chain") */}
          {/* a Convert happens inside the exchange: no chain to show (Nov 3 2026) */}
          {!convertMode && track?.kind !== 'conversion' ? <ChainChip imageUrl={sol.imageUrl} /> : null}
          {!tracking && platformOptions.length > 0 ? (
            <>
              <HeaderRule />
              <PlatformGroup value={convertMode ? exchangeId : custody} options={convertMode ? convertOptions : platformOptions} disabled={setCustody.isPending} onChange={pickPlatform} />
            </>
          ) : null}
        </span>
      }
      footer={footer}
    >
      {/* The form stays on screen for the whole swap; progress shows underneath it. A flow opened from its chip shows the progress alone. */}
      <div className={`grid gap-2 *:min-w-0 ${busy ? 'pointer-events-none opacity-60' : ''} ${tracking ? 'hidden' : ''}`} aria-busy={busy} aria-hidden={tracking}>
        {/* You pay */}
        <div className="card grid gap-3 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">You pay</span>
            <span className="flex items-center gap-2 text-xs text-accent">
              {[25, 50, 100].map((pct) => (
                <button key={pct} type="button" onClick={() => setAmount(trim((payBalance * pct) / 100, Math.min(payDecimals, 6)))}>{pct === 100 ? 'Max' : `${pct}%`}</button>
              ))}
              <TradeSettings />
            </span>
          </div>
          <div className="flex items-center gap-3">
            <NumericInput className="w-0 min-w-0 flex-1 bg-transparent text-3xl font-semibold outline-none placeholder:text-muted" placeholder="0" value={amount} onChange={setAmount} readOnly={busy} />
            <TokenPicker
              value={pay}
              onChange={(t) => {
                setPay(t)
                setAmount('')
                // selling a token: the other side is SOL unless the user already chose
                if (!t.isNative && (!receive || receive.address === t.address)) setReceive(sol)
                if (t.isNative && receive?.isNative) setReceive(null)
              }}
              holdings={accountHoldings}
              allowSearch={false}
              heldOnly
              showSymbol
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted">
            <span>{payUsd !== undefined ? formatUsd(payUsd) : '$0.00'}</span>
            <span className="flex items-center gap-2">
              Balance {formatTokenAmount(payHeld, pay?.symbol)}
            </span>
          </div>
        </div>
        <div className="-my-4 flex justify-center">
          <button type="button" onClick={flip} className="z-10 flex size-9 items-center justify-center rounded-full border border-line bg-surface-2 text-muted transition-colors hover:text-text" title="Flip">
            <ArrowDown className="size-4" />
          </button>
        </div>
        {/* Receive */}
        <div className="card grid gap-3 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Receive</span>
            {estimate.isFetching ? <Spinner className="size-4" /> : null}
          </div>
          <div className="flex items-center gap-3">
            <div className={`min-w-0 flex-1 truncate text-3xl font-semibold ${estimatedOut === undefined ? 'text-muted' : ''}`}>
              {estimatedOut !== undefined ? formatTokenAmount(estimatedOut, receive?.symbol) : '0'}
            </div>
            <TokenPicker value={receive} onChange={setReceive} holdings={accountHoldings} allowSearch showSymbol exchangeCoins={onExchange} />
          </div>
          {/* same footer row as You pay; the SOL/USDC/USDT shortcut chips that sat here are gone, the dropdown already lists them (John, Nov 2 2026) */}
          <div className="flex items-center justify-between text-xs text-muted">
            <span>{receiveUsd !== undefined ? formatUsd(receiveUsd) : '$0.00'}</span>
            {receive ? <span>Balance {formatTokenAmount(receiveHeld, receive.symbol)}</span> : null}
          </div>
        </div>
        {phase.kind === 'form' && impactWarns(impactPct, payUsd) ? (
          <p className="text-xs text-warning">Price impact {formatPct(impactPct, false)} on this size.</p>
        ) : null}
        {phase.kind === 'form' && side === 'sell' && cleanSale.data?.short ? (
          <div className="rounded-control border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
            <span className="font-medium">This sale cannot clear the insider mark yet.</span> {cleanSale.data.problem}
          </div>
        ) : null}
        {phase.kind === 'form' && !convertMode && estimate.isError ? <p className="text-xs text-negative">No route found for this pair and size.</p> : null}
        {phase.kind === 'form' && convertMode && convertQuote.isError ? <p className="text-xs text-negative">{errorMessage(convertQuote.error)}</p> : null}
        {phase.kind === 'form' && convertMode && convertQuote.data ? (
          <p className="text-xs text-muted">
            1 {payCoin} ≈ {formatTokenAmount(convertQuote.data.rate, receiveCoin ?? undefined)} {receiveCoin} at {CUSTODY_LABELS[exchangeId]}, fee {(convertQuote.data.feeBps / 100).toFixed(2)}%
          </p>
        ) : null}
      </div>

      {/* Progress, in place */}
      {phase.kind === 'convert' ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm">
          {cv?.status === 'completed' ? (
            <FlowDone
              title="Conversion complete"
              amount={`${formatTokenAmount(cv.fromAmountUi ?? 0, cv.from)} ${cv.from} → ${formatTokenAmount(cv.toAmountUi ?? 0, cv.to)} ${cv.to}`}
              note={`at ${CUSTODY_LABELS[cv.platform as ExchangeId] ?? cv.platform}, no chain touched`}
            >
              <FeeReceipt items={cv.fees} className="border-t border-line pt-3" />
            </FlowDone>
          ) : cv?.status === 'failed' || cv?.status === 'stalled' ? (
            <p className="text-negative">{cv.error ?? 'The conversion did not go through.'}</p>
          ) : (
            <>
              <ProgressBar value={cv ? 20 + (70 * cv.legs.filter((leg) => leg.filled).length) / Math.max(1, cv.legs.length) : 10} />
              <div>
                <div className="font-medium">Converting…</div>
                <div className="text-xs text-muted">{cv ? `${cv.legs.filter((leg) => leg.filled).length} of ${cv.legs.length} orders filled at ${CUSTODY_LABELS[cv.platform as ExchangeId] ?? cv.platform}` : 'Placing the order…'}</div>
              </div>
            </>
          )}
        </div>
      ) : phase.kind === 'quoting' ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm"><ProgressBar value={30} /><div><div className="font-medium">Getting the best route…</div><div className="text-xs text-muted">Clean quotes swap straight away; risky ones stop here for a look.</div></div></div>
      ) : phase.kind === 'review' && s ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm">
          <div className="rounded-control border border-warning/40 bg-warning/10 p-3">
            <div className="mb-1 font-medium text-warning">Check before swapping</div>
            <ul className="grid gap-1 text-xs text-warning">{s.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
          <DetailList
            rows={[
              { label: 'You pay', value: `${formatTokenAmount(s.inputAmount?.uiAmount ?? 0, s.inputTokenSymbol)} ${s.inputTokenSymbol ?? ''}`, hint: s.inputAmount?.usdValue ? formatUsd(s.inputAmount.usdValue) : null },
              { label: 'You receive (est.)', value: `${formatTokenAmount(s.estimatedOutputAmount?.uiAmount ?? 0, s.outputTokenSymbol)} ${s.outputTokenSymbol ?? ''}`, hint: s.estimatedOutputAmount?.usdValue ? formatUsd(s.estimatedOutputAmount.usdValue) : null },
              { label: 'Price impact', value: <span className={impactWarns(s.priceImpactPct, s.inputAmount?.usdValue) ? 'text-warning' : ''}>{formatPct(s.priceImpactPct, false)}</span> },
              { label: 'Network fee', value: s.networkFeeUsd !== undefined ? formatUsd(s.networkFeeUsd) : '–' },
              { label: 'Slippage', value: s.slippageBps !== undefined ? `${(s.slippageBps / 100).toFixed(2)}%` : '–' },
            ]}
          />
          {expiresIn !== null ? <p className="text-xs text-muted">Quote valid for {expiresIn}s.</p> : null}
        </div>
      ) : phase.kind === 'sending' ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm"><ProgressBar value={s?.transactionId ? 85 : 65} /><div><div className="font-medium">Swapping…</div><div className="text-xs text-muted">{s?.transactionId ? 'Transaction sent, waiting for confirmation.' : 'Signing and sending through Jupiter.'}</div></div></div>
      ) : phase.kind === 'done' ? (
        <div className="card mt-3 p-4 text-sm">
          <FlowDone
            title="Swap complete"
            amount={`${formatTokenAmount(s?.inputAmount?.uiAmount ?? 0, s?.inputTokenSymbol)} ${s?.inputTokenSymbol ?? ''} → ${s?.outputAmount ? '' : '≈'}${formatTokenAmount((s?.outputAmount ?? s?.estimatedOutputAmount)?.uiAmount ?? 0, s?.outputTokenSymbol)} ${s?.outputTokenSymbol ?? ''}`}
            note={s && !s.checked ? 'Confirming the final amount…' : undefined}
            link={s?.transactionId ? { href: `https://solscan.io/tx/${s.transactionId}` } : null}
          >
            <RouteLine route={s?.route} className="border-t border-line pt-3" />
            <FeeReceipt items={s?.fees} className="border-t border-line pt-3" />
          </FlowDone>
        </div>
      ) : showRail ? (
        <div className="mt-3">
          <PositionProgress
            positionId={phase.kind === 'position' ? phase.positionId : null}
            mode={phase.kind === 'position' ? phase.mode : railFlow === 'vaultSell' ? 'sell' : 'open'}
            flow={railFlow ?? undefined}
            exchange={onExchange}
            house={onExchange && (me?.presets.removeInsiderMark ?? true)}
            startedAt={flowStartedAt}
            starting={{ failed: startFailed, error, onBack: () => { setStartFailed(false); setError(null); setRailFlow(null) } }}
            onSettled={() => setPositionSettled(true)}
            onStatus={setPositionStatus}
          />
        </div>
      ) : null}
      {error && !showRail ? <p className="mt-2 text-sm text-negative">{error}</p> : null}
    </Modal>
  )
}

